import * as Yup from "yup";
import {
  validatePhoneNumber,
  buildMaxErrorMessage,
  buildErrorMsg,
} from "component-library";
import { IJoiningPagesCommon } from "translations/src/models/joining-pages/Common";
import { ISignupForm } from "translations/src/models/joining-pages/LoginPage";

import {
  URL_VALIDATION,
  SOCIAL_MEDIA_HANDLE,
} from "../../../../helpers/validation-helpers";

import { IBooleanValue } from "../../../../formModels/commonFormTypes";
import { IBillingAddress } from "../../../../services/online-application.types";

export interface INamesForm {
  firstNameCD: string;
  secondNameCD: string;
}

export interface IWelcomePageFormRestFields extends IBillingAddress {
  mobileNumberCD: string;
  landlineNumberCD: string;
  emailCD: string;
  occupation: string | number;
  companyName: string;
  companyWebsite: string;
  memberOfProfessionalOrg: IBooleanValue;
  professionalOrgMembershipType: string;
  professionalOrgMembershipName: string;
  professionalOrgMembershipEmail: string;
  professionalOrgMembershipNumber: string;
  hadSpotlightAccount: IBooleanValue;
  existingSpotlightAccName: string;
  existingSpotlightAccMembershipType: string;
  existingSpotlightAccEmail: string;
  termsAgree: boolean;
}

export type WelcomePageFormFields = INamesForm & IWelcomePageFormRestFields;

export const welcomePageRestFieldsInitialValues: IWelcomePageFormRestFields = {
  mobileNumberCD: "+44",
  landlineNumberCD: "+44",
  emailCD: "",
  occupation: "",
  companyName: "",
  companyWebsite: "",
  memberOfProfessionalOrg: null,
  professionalOrgMembershipType: "",
  professionalOrgMembershipName: "",
  professionalOrgMembershipEmail: "",
  professionalOrgMembershipNumber: "",
  hadSpotlightAccount: null,
  existingSpotlightAccName: "",
  existingSpotlightAccMembershipType: "",
  existingSpotlightAccEmail: "",
  termsAgree: false,
  street1: "",
  street2: "",
  city: "",
  country: "",
  state_province: "",
  postal_code: "",
};

const namesFormsInitialFields = { firstNameCD: "", secondNameCD: "" };

export const welcomePageFormInitialFields = {
  ...namesFormsInitialFields,
  ...welcomePageRestFieldsInitialValues,
};

export const getWelcomePageFullFormValidation = (
  fieldNames: ISignupForm,
  common: IJoiningPagesCommon
) => {
  const { entryRequired, entryNotValid, entryMaxLength } = common.errorsCommon;

  return Yup.object().shape({
    mobileNumberCD: Yup.string()
      .trim()
      .required()
      .test(
        "validateMobile",
        buildErrorMsg(entryNotValid, fieldNames.signupMobileLabel),
        (val) => validatePhoneNumber("mobile", val)
      ),
    landlineNumberCD: Yup.string()
      .trim()
      .test(
        "validateLandline",
        buildErrorMsg(entryNotValid, fieldNames.signupLandlineLabel),
        (val) => validatePhoneNumber("landline", val)
      ),
    emailCD: Yup.string()
      .trim()
      .required(buildErrorMsg(entryRequired, fieldNames.signupEmailLabel))
      .email(buildErrorMsg(entryNotValid, fieldNames.signupEmailLabel))
      .max(
        50,
        buildMaxErrorMessage(entryMaxLength, fieldNames.signupEmailLabel, 50)
      ),
    occupation: Yup.string().required(
      buildErrorMsg(entryRequired, fieldNames.signupYourOccupationLabel)
    ),
    companyName: Yup.string()
      .trim()
      .required(buildErrorMsg(entryRequired, fieldNames.signupCompanyNameLabel))
      .matches(
        /^[a-zA-Z0-9\s]+$/,
        buildErrorMsg(entryNotValid, fieldNames.signupCompanyNameLabel)
      )
      .max(
        50,
        buildMaxErrorMessage(
          entryMaxLength,
          fieldNames.signupCompanyNameLabel,
          50
        )
      ),
    companyWebsite: Yup.string()
      .nullable()
      .trim()
      .test(
        "validateWebsite",
        buildErrorMsg(entryNotValid, fieldNames.signupCompanyWebsiteLabel),
        (val) =>
          !val ||
          URL_VALIDATION.test(val ?? "") ||
          SOCIAL_MEDIA_HANDLE.test(val ?? "")
      )
      .max(
        50,
        buildMaxErrorMessage(
          entryMaxLength,
          fieldNames.signupCompanyWebsiteLabel,
          50
        )
      ),

    memberOfProfessionalOrg: Yup.string()
      .required()
      .typeError(
        buildErrorMsg(
          entryRequired,
          fieldNames.signupMemberOfProfessionalOrgLabel
        )
      ),
    professionalOrgMembershipType: Yup.string().when(
      "memberOfProfessionalOrg",
      {
        is: "yes",
        then: Yup.string()
          .trim()
          .required(
            buildErrorMsg(entryRequired, fieldNames.signupPleaseSpecifyLabel)
          )
          .max(
            64,
            buildMaxErrorMessage(
              entryMaxLength,
              fieldNames.signupPleaseSpecifyLabel,
              64
            )
          ),
      }
    ),
    professionalOrgMembershipName: Yup.string().when(
      "memberOfProfessionalOrg",
      {
        is: "yes",
        then: Yup.string()
          .trim()
          .required(
            buildErrorMsg(entryRequired, fieldNames.signupMembershipNameLabel)
          )
          .max(
            64,
            buildMaxErrorMessage(
              entryMaxLength,
              fieldNames.signupMembershipNameLabel,
              64
            )
          ),
      }
    ),
    professionalOrgMembershipEmail: Yup.string().when(
      "memberOfProfessionalOrg",
      {
        is: "yes",
        then: Yup.string()
          .trim()
          .email(
            buildErrorMsg(entryNotValid, fieldNames.signupMembershipEmailLabel)
          )
          .required(
            buildErrorMsg(entryRequired, fieldNames.signupMembershipEmailLabel)
          )
          .max(
            50,
            buildMaxErrorMessage(
              entryMaxLength,
              fieldNames.signupMembershipEmailLabel,
              64
            )
          ),
      }
    ),
    professionalOrgMembershipNumber: Yup.string().when(
      "memberOfProfessionalOrg",
      {
        is: "yes",
        then: Yup.string()
          .trim()
          .max(
            30,
            buildMaxErrorMessage(
              entryMaxLength,
              fieldNames.signupCurrentMembershipNumberLabel,
              64
            )
          ),
      }
    ),
    hadSpotlightAccount: Yup.string()
      .required()
      .typeError(
        buildErrorMsg(entryRequired, fieldNames.signupHadSpotlightAccountLabel)
      ),
    existingSpotlightAccName: Yup.string().when("hadSpotlightAccount", {
      is: "yes",
      then: Yup.string()
        .trim()
        .required(
          buildErrorMsg(entryRequired, fieldNames.signupMembershipNameLabel)
        )
        .max(
          64,
          buildMaxErrorMessage(
            entryMaxLength,
            fieldNames.signupMembershipNameLabel,
            64
          )
        ),
    }),
    existingSpotlightAccMembershipType: Yup.string().when(
      "hadSpotlightAccount",
      {
        is: "yes",
        then: Yup.string()
          .trim()
          .required(buildErrorMsg(entryRequired, fieldNames.memberTypeLabel)),
      }
    ),
    existingSpotlightAccEmail: Yup.string().when("hadSpotlightAccount", {
      is: "yes",
      then: Yup.string()
        .trim()
        .required(
          buildErrorMsg(
            entryRequired,
            fieldNames.signupExistingSpotlightAccEmailLabel
          )
        )
        .email(
          buildErrorMsg(
            entryNotValid,
            fieldNames.signupExistingSpotlightAccEmailLabel
          )
        )
        .max(
          50,
          buildMaxErrorMessage(
            entryMaxLength,
            fieldNames.signupExistingSpotlightAccEmailLabel,
            50
          )
        ),
    }),
    termsAgree: Yup.boolean().oneOf(
      [true],
      buildErrorMsg(entryRequired, fieldNames.signupTermsErrorLabel)
    ),
  });
};
